import React, { useState, useEffect } from "react";
import { Button, Grid, Steps, message } from "antd";
import { ChevronLeftIcon, ChevronRightIcon } from "@heroicons/react/24/outline";
import { ArrowRightOutlined, ArrowLeftOutlined } from "@ant-design/icons";
import SidePreviewScreen from "../_layouts/SidePreviewScreen";
import { LOGO_ONLY } from "../../src/constants";
import BusinessInfo from "../business_form/slides/BusinessInfo";
import IndustryInfo from "../business_form/slides/IndustryInfo";
import BusinessUSP from "../business_form/slides/BusinessUSP";
import BusinessAudience from "../business_form/slides/BusinessAudience";
import { useCreateItem, useUpdateItem } from "../_actions/mutations/items";
import { useBusiness } from "../_actions/finance";
import { atom, useAtom, useSetAtom } from "jotai";
import { showcaseOrgAtom } from "../../src/onboarding/atoms";
import styles from "../business_form/styles/BusinessForm.module.css";
import { nanoid } from "nanoid";
import { snakeCase } from "lodash";
import BrandAssists from "../business_form/slides/BrandAssets";
import BrandSocials from "../business_form/slides/BrandSocials";

const { useBreakpoint } = Grid;

export const orgOBAtom = atom({});

const toSnakeCase = (str) => {
    return str
        .toLowerCase()
        .replace(/[^a-z0-9]/g, "_")
        .replace(/_+/g, "_")
        .replace(/^_|_$/g, "");
};

const generateUniqueId = (baseName) => {
    const timestamp = Date.now().toString(36);
    const random = nanoid(8);
    const snakeCaseName = toSnakeCase(baseName);
    return `${snakeCaseName}_${timestamp}_${random}`;
};

function BrandOrganizationSetup({ dataMe, onBack, onDone }) {
    const screens = useBreakpoint();
    const isMobile = !screens.sm;
    const [currentSlide, setCurrentSlide] = useState(0);
    const [answers, setAnswers] = useState({});
    const [loading, setLoading] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [org_id, setOrgId] = useState(null);

    const [nextBtnProps, setNextBtnProps] = useState({
        // loading: true
    });
    const [orgOB, setOrgOB] = useAtom(orgOBAtom);
    useEffect(() => {
        if (currentSlide !== 0) {
            setNextBtnProps({});
            setOrgOB({ step: null });
        }
    }, [currentSlide]);

    const slides = [
        { component: BusinessInfo, name: "Information" },
        { component: IndustryInfo, name: "Industry" },
        { component: BusinessUSP, name: "USP" },
        { component: BusinessAudience, name: "Audience" },
        { component: BrandAssists, name: "BrandAssist" },
        { component: BrandSocials, name: "Communication" },
    ];

    const setShowcaseOrg = useSetAtom(showcaseOrgAtom);

    const { mutate: Businessdata } = useBusiness(
        "business/process_description",
        (response) => {
            const apiData = response.data;
            setAnswers((prev) => ({
                ...prev,
                name: apiData.name,
                businessDescription: apiData.description,
                businessModel: apiData.business_model,
                industry: apiData.industry,
                businessUSP: apiData.USP,
                targetInterests: apiData.brand_vibes,
                targetAudience: apiData.target_audience,
            }));
            setCurrentSlide(currentSlide + 1);
            setLoading(false);
        },
        (error) => {
            console.error("API error:", error);
            message.error(
                "Failed to process business information. Please try again."
            );
            setLoading(false);
        }
    );

    // Updated mutation hooks to match the previous version
    const mutationCreate = useCreateItem("qdb_organization", (result) => {
        console.log(result, "Org Created");
        setOrgId(result.id);
        setShowcaseOrg(result);
        message.success("Organization created successfully!");
        setSubmitting(false);
        onDone();
    });

    const mutationUpdate = useUpdateItem(
        "qdb_organization",
        org_id,
        (result) => {
            console.log(result, "Org Updated");
            message.success("Organization updated successfully!");
            setSubmitting(false);
            onDone();
        }
    );

    const handleAnswerChange = (key, value) => {
        setAnswers((prev) => ({
            ...prev,
            [key]: value,
        }));
    };

    const nextSlide = (status, apiData) => {
        if (orgOB?.step === "process_url") {
            setOrgOB({
                ...orgOB,
                action: "process_url",
                random: nanoid(9),
            });
            // return;
        }
        if (status === "publish") {
            //better comparison
            if (currentSlide === 0) {
                if (apiData || answers.apiData) {
                    const dataToUse = apiData || answers.apiData;
                    setAnswers((prev) => ({
                        ...prev,
                        name: dataToUse.name,
                        businessModel: dataToUse.businessModel,
                        industry: dataToUse.industry,
                        businessUSP: dataToUse.businessUSP,
                        targetInterests: dataToUse.targetInterests,
                        targetAudience: dataToUse.targetAudience,
                    }));
                    setCurrentSlide(currentSlide + 1);
                } else {
                    setLoading(true);
                    Businessdata({
                        content: answers.businessDescription,
                        selected_org: dataMe.selected_org?.id,
                    });
                }
            } else {
                if (currentSlide < slides.length - 1) {
                    setCurrentSlide(currentSlide + 1);
                }
            }
        } else {
            if (currentSlide < slides.length - 1) {
                setCurrentSlide(currentSlide + 1);
            }
        }
    };

    const prevSlide = () => {
        if (currentSlide > 0) {
            setCurrentSlide(currentSlide - 1);
        }
    };

    const handleSubmit = () => {
        setSubmitting(true);

        const organizationName = answers.name || "New Organization";

        // Use the same ID generation logic as the previous version
        const organizationData = {
            name: organizationName,
            description: answers.businessDescription,
            business_model: answers.businessModel,
            industry: answers.industry,
            USP: answers.businessUSP, // Changed from 'usp' to 'USP' to match API response
            target_audience: answers.targetAudience,
            brand_vibes: answers.targetInterests,
            logo: answers.mainLogo,
            logo_square: answers.squareLogo,
            logo_dark: answers.darkLogo,
            primary_phone: answers.phone,
            primary_email: answers.email,
            address: answers.address,
            website: answers.website,
            brand_colors: answers.brandColors,
            color_palettes: answers.colorPalettes,

            linkedin_handle: answers.socialHandles?.linkedin,
            instagram_handle: answers.socialHandles?.instagram,
            facebook_handle: answers.socialHandles?.facebook,
            youtube_handle: answers.socialHandles?.youtube,
            pinterest_handle: answers.socialHandles?.pinterest,
            owner: dataMe.id,
            users: [{ directus_users_id: dataMe.id }], // Add user relationship like previous version
            is_personal: false, // Add this field like previous version
        };

        if (org_id) {
            // Update existing organization
            mutationUpdate.mutate(organizationData);
        } else {
            // Create new organization with generated ID
            const finalData = {
                ...organizationData,
                id: `${snakeCase(organizationName)}_${nanoid(5)}`, // Use same ID generation as previous version
            };

            console.log(finalData, "Creating org with");
            mutationCreate.mutate(finalData);
        }
    };

    const CurrentSlideComponent = slides[currentSlide].component;

    return (
        <SidePreviewScreen
            title="Set up your Organization"
            subtitle="Tell us about your business to create better designs"
            logo={LOGO_ONLY}
            styles_card={{
                maxWidth: "100%",
                width: isMobile ? "100%" : "800px",
            }}
        >
            <div className={styles.contentWrapper}>
                {/* Progress Bar */}
                {currentSlide > 0 && (
                    <div className={styles.stepsContainer}>
                        <Steps
                            current={currentSlide - 1}
                            size="small"
                            responsive={false}
                            items={slides.slice(1).map((slide, index) => ({
                                status:
                                    index < currentSlide - 1
                                        ? "finish"
                                        : index === currentSlide - 1
                                        ? "process"
                                        : "wait",
                            }))}
                        />
                    </div>
                )}

                {/* Current Slide */}
                <div className={styles.slideContainer}>
                    <CurrentSlideComponent
                        dataMe={dataMe}
                        answers={answers}
                        onAnswerChange={handleAnswerChange}
                        onNextSlide={(apiData) => nextSlide("publish", apiData)}
                        setNextButtonProps={setNextBtnProps}
                    />
                </div>

                {/* Navigation */}
                <div className={styles.navigation}>
                    <Button
                        onClick={currentSlide === 0 ? onBack : prevSlide}
                        icon={<ArrowLeftOutlined />}
                        size={isMobile ? "large" : "middle"}
                    >
                        {currentSlide === 0 ? "Back" : "Previous"}
                    </Button>

                    <div style={{ display: "flex", gap: "10px" }}>
                        {currentSlide < slides.length - 1 && (
                            <Button
                                onClick={() => nextSlide("draft")}
                                size={isMobile ? "large" : "middle"}
                            >
                                Skip
                            </Button>
                        )}

                        {currentSlide === slides.length - 1 ? (
                            <Button
                                type="primary"
                                onClick={handleSubmit}
                                loading={
                                    submitting ||
                                    mutationCreate.isLoading ||
                                    mutationUpdate.isLoading
                                }
                                disabled={
                                    submitting ||
                                    mutationCreate.isLoading ||
                                    mutationUpdate.isLoading
                                }
                                icon={<ArrowRightOutlined />}
                                iconPosition="end"
                                size={isMobile ? "large" : "middle"}
                            >
                                Complete Setup
                            </Button>
                        ) : (
                            <Button
                                type="primary"
                                onClick={() => nextSlide("publish")}
                                loading={currentSlide === 0 && loading}
                                icon={<ArrowRightOutlined />}
                                iconPosition="end"
                                size={isMobile ? "large" : "middle"}
                                {...nextBtnProps}
                            >
                                {nextBtnProps?.text || "Next"}
                            </Button>
                        )}
                    </div>
                </div>
            </div>
        </SidePreviewScreen>
    );
}

export default BrandOrganizationSetup;
